import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';
import FloatingLineButton from '../home/components/FloatingLineButton';
import JobEditForm from './components/JobEditForm';
import { fetchFeaturedImageUrl } from '../../lib/wordpress';

interface Job {
  id: number;
  facilityType: string;
  facilityName: string;
  location: string;
  address: string;
  access: string;
  hourlyWage: string;
  workingHours: string;
  holidays: string;
  tags: string[];
  benefits: string[];
  requirements: string[];
  jobDescription: string;
  applicationMethod: string;
  image: string;
  contentHtml: string;
}

interface WordPressJob {
  id: number;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  featured_media: number;
  acf: {
    location: string;
    station: string;
    work_point: string;
    salary: string;
    work_time: string;
    holiday: string;
    benefits: string;
    qualification: string;
    application: string;
    facility_type?: string;
    address?: string;
    image?: string;
    images?: Array<{ url: string; alt?: string }>;
  };
}

export default function JobDetail() {
  const { id } = useParams();
  const [showEditForm, setShowEditForm] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [job, setJob] = useState<Job | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acfImages, setAcfImages] = useState<Array<{ url: string; alt?: string }>>([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kaigo.trust-job.com';

  // ページ遷移時に最上部にスクロール
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Schema.org JobPosting & meta tags
  useEffect(() => {
    if (!job) return;

    const title = `${job.facilityName}｜${job.location}の介護派遣求人｜トラスト`;
    document.title = title;

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    const desc = `${job.facilityName}（${job.location}）の介護派遣求人。時給${job.hourlyWage}、${job.workingHours}。ブランクOK・週3日から勤務可能。名古屋の介護派遣トラストが全力サポート。`;
    setMeta('description', desc);
    setMeta('og:title', title, true);
    setMeta('og:description', desc, true);
    setMeta('og:url', `${siteUrl}/job/${id}`, true);

    // Schema.org JobPosting
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'JobPosting',
      title: job.facilityName,
      description: job.jobDescription || job.contentHtml || desc,
      datePosted: new Date().toISOString().split('T')[0],
      employmentType: 'TEMPORARY',
      hiringOrganization: {
        '@type': 'Organization',
        name: '介護派遣トラスト',
        sameAs: siteUrl,
        telephone: '052-887-6243',
      },
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: job.address || job.location,
          addressLocality: '名古屋市',
          addressRegion: '愛知県',
          addressCountry: 'JP',
        },
      },
      baseSalary: {
        '@type': 'MonetaryAmount',
        currency: 'JPY',
        value: {
          '@type': 'QuantitativeValue',
          description: job.hourlyWage,
          unitText: 'HOUR',
        },
      },
      industry: '介護・福祉',
      occupationalCategory: '介護職',
      url: `${siteUrl}/job/${id}`,
    };

    document.querySelectorAll('script[data-schema="job-detail"]').forEach((s) => s.remove());
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.dataset.schema = 'job-detail';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      document.querySelectorAll('script[data-schema="job-detail"]').forEach((s) => s.remove());
    };
  }, [job, id, siteUrl]);

  // WordPress REST APIから求人データを取得
  useEffect(() => {
    async function loadJob() {
      if (!id) {
        setLoading(false);
        setError('求人IDが指定されていません');
        return;
      }

      try {
        const response = await fetch(`https://trust-job.com/wp-json/wp/v2/jobs/${id}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            setError('指定された求人が見つかりませんでした');
          } else {
            setError(`データの取得に失敗しました（ステータス: ${response.status}）`);
          }
          setLoading(false);
          return;
        }
        
        const data: WordPressJob = await response.json();

        let featuredImageUrl = '';
        if (data.featured_media) {
          const url = await fetchFeaturedImageUrl(data.featured_media);
          if (url) featuredImageUrl = url;
        }

        if (data.acf?.images && Array.isArray(data.acf.images) && data.acf.images.length > 0) {
          setAcfImages(data.acf.images);
        }

        const mainImage = featuredImageUrl
          || data.acf?.image
          || 'https://public.readdy.ai/ai/img_res/73bf5e3fe1ac27f3f310e32de05493cd.jpg';
        
        const convertedJob: Job = {
          id: data.id,
          facilityType: data.acf?.facility_type || 'デイサービス',
          facilityName: data.title?.rendered || '施設名未設定',
          location: data.acf?.location || '',
          address: data.acf?.address || data.acf?.location || '',
          access: data.acf?.station || '',
          hourlyWage: data.acf?.salary || '',
          workingHours: data.acf?.work_time || '',
          holidays: data.acf?.holiday || '',
          tags: data.acf?.work_point ? data.acf.work_point.split('、').map(t => t.trim()) : [],
          benefits: data.acf?.benefits ? data.acf.benefits.split('\n').filter(b => b.trim()) : [],
          requirements: data.acf?.qualification ? data.acf.qualification.split('\n').filter(r => r.trim()) : [],
          jobDescription: data.acf?.work_point || '',
          applicationMethod: data.acf?.application || '',
          image: mainImage,
          contentHtml: data.content?.rendered || '',
        };
        
        setJob(convertedJob);
        setError(null);
      } catch (err) {
        console.error('求人データの読み込みに失敗しました:', err);
        setError('ネットワークエラーが発生しました。インターネット接続を確認してください。');
      } finally {
        setLoading(false);
      }
    }

    loadJob();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">求人情報を読み込み中...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-red-500">
            <i className="ri-error-warning-line text-6xl"></i>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {error || '求人が見つかりません'}
          </h1>
          <p className="text-gray-600 mb-8">
            {error ? 'もう一度お試しいただくか、トップページから求人を探してください。' : ''}
          </p>
          <button
            onClick={() => window.REACT_APP_NAVIGATE('/')}
            className="bg-blue-600 text-white px-8 py-3 rounded-full font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            トップページに戻る
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const handlePasswordSubmit = () => {
    if (password === '00350228') {
      setShowEditForm(true);
      setShowPasswordInput(false);
      setPasswordError('');
    } else {
      setPasswordError('パスワードが正しくありません');
    }
  };

  const handleAdminLongPress = () => {
    let pressTimer: NodeJS.Timeout;
    
    const startPress = () => {
      pressTimer = setTimeout(() => {
        setShowPasswordInput(true);
      }, 1000);
    };

    const cancelPress = () => {
      clearTimeout(pressTimer);
    };

    return { startPress, cancelPress };
  };

  const { startPress, cancelPress } = handleAdminLongPress();

  const handleJobUpdate = async (updatedJob: Job) => {
    try {
      setJob(updatedJob);
      setShowEditForm(false);
      setPassword('');
      
      alert('求人情報を更新しました。');
    } catch (error) {
      console.error('求人情報の更新に失敗しました:', error);
      alert('求人情報の更新に失敗しました。もう一度お試しください。');
    }
  };

  // 画像スワイプ用のハンドラー
  const allImages = acfImages.length > 0
    ? acfImages.map(img => img.url)
    : job ? [job.image] : [];

  const handlePrevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {showEditForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-start justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-4xl bg-white rounded-lg shadow-xl my-8">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10">
              <h2 className="text-xl font-bold text-gray-900">求人情報を編集</h2>
            </div>
            <div className="p-6 max-h-none">
              <JobEditForm
                job={job!}
                onUpdate={handleJobUpdate}
                onClose={() => setShowEditForm(false)}
              />
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <button
          onClick={() => window.REACT_APP_NAVIGATE('/')}
          className="flex items-center text-blue-600 hover:text-blue-700 mb-8 cursor-pointer whitespace-nowrap"
        >
          <div className="w-5 h-5 flex items-center justify-center mr-2">
            <i className="ri-arrow-left-line text-xl"></i>
          </div>
          <span className="font-medium">求人一覧に戻る</span>
        </button>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
          <div className="relative">
            <div className="w-full h-96 bg-gray-100 relative overflow-hidden">
              {allImages.length > 0 && (
                <img 
                  src={allImages[currentImageIndex]}
                  alt={job?.facilityName || '求人画像'}
                  className="w-full h-full object-contain"
                />
              )}
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={handlePrevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full shadow-lg w-10 h-10 flex items-center justify-center cursor-pointer z-10"
                    aria-label="前の画像"
                  >
                    <i className="ri-arrow-left-s-line text-2xl text-blue-500"></i>
                  </button>
                  <button
                    onClick={handleNextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full shadow-lg w-10 h-10 flex items-center justify-center cursor-pointer z-10"
                    aria-label="次の画像"
                  >
                    <i className="ri-arrow-right-s-line text-2xl text-blue-500"></i>
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                    {allImages.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`w-3 h-3 rounded-full cursor-pointer transition-all ${
                          currentImageIndex === idx ? 'bg-blue-500 scale-110' : 'bg-white/70'
                        }`}
                        aria-label={`画像 ${idx + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="p-8 sm:p-12">
            {/* WordPress本文がある場合に表示 */}
            {job!.contentHtml && (
              <div
                className="prose prose-gray max-w-none mb-8 text-base text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: job!.contentHtml }}
              />
            )}

            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center mr-3 mt-1">
                  <i className="ri-map-pin-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">勤務地</div>
                  <div className="text-lg font-medium text-gray-900">
                    {job!.location.split('\n').map((line, index) => (
                      <div key={index}>
                        {line}
                        {index < job!.location.split('\n').length - 1 && <br />}
                      </div>
                    ))}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    {job!.access.split('\n').map((line, index) => (
                      <div key={index}>
                        {line}
                        {index < job!.access.split('\n').length - 1 && <br />}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-6 h-6 flex items-center justify-center mr-3 mt-1">
                  <i className="ri-money-dollar-circle-line text-2xl text-blue-600"></i>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-2">給与</div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                    <div className="text-lg font-bold text-blue-700">
                      {job!.hourlyWage.split('\n').map((line, index) => (
                        <div key={index}>
                          {line}
                          {index < job!.hourlyWage.split('\n').length - 1 && <br />}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              {job!.jobDescription && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <div className="w-6 h-6 flex items-center justify-center mr-3">
                      <i className="ri-star-line text-2xl text-blue-600"></i>
                    </div>
                    お仕事ポイント
                  </h2>
                  <div className="text-base text-gray-700 leading-relaxed pl-9">
                    {job!.jobDescription.split('\n').map((line, index) => (
                      <div key={index}>
                        {line}
                        {index < job!.jobDescription.split('\n').length - 1 && <br />}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-3">
                    <i className="ri-time-line text-2xl text-blue-600"></i>
                  </div>
                  勤務時間
                </h2>
                <div className="text-base text-gray-700 pl-9">
                  {job!.workingHours.split('\n').map((line, index) => (
                    <div key={index}>
                      {line}
                      {index < job!.workingHours.split('\n').length - 1 && <br />}
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-3">
                    <i className="ri-calendar-line text-2xl text-blue-600"></i>
                  </div>
                  休日
                </h2>
                <div className="text-base text-gray-700 pl-9">
                  {job!.holidays.split('\n').map((line, index) => (
                    <div key={index}>
                      {line}
                      {index < job!.holidays.split('\n').length - 1 && <br />}
                    </div>
                  ))}
                </div>
              </div>

              {job!.benefits.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <div className="w-6 h-6 flex items-center justify-center mr-3">
                      <i className="ri-gift-line text-2xl text-blue-600"></i>
                    </div>
                    待遇・福利厚生
                  </h2>
                  <ul className="space-y-2 pl-9">
                    {job!.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-center text-base text-gray-700">
                        <span className="text-blue-600 mr-2">✓</span>
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {job!.requirements.length > 0 && (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <div className="w-6 h-6 flex items-center justify-center mr-3">
                      <i className="ri-user-star-line text-2xl text-blue-600"></i>
                    </div>
                    応募資格
                  </h2>
                  <ul className="space-y-2 pl-9">
                    {job!.requirements.map((req, index) => (
                      <li key={index} className="flex items-center text-base text-gray-700">
                        <span className="text-blue-600 mr-2">✓</span>
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-3">
                    <i className="ri-mail-send-line text-2xl text-blue-600"></i>
                  </div>
                  応募方法
                </h2>
                <div className="text-base text-gray-700 mb-6 pl-9">
                  {job!.applicationMethod.split('\n').map((line, index) => (
                    <div key={index}>
                      {line}
                      {index < job!.applicationMethod.split('\n').length - 1 && <br />}
                    </div>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-4 pl-9">
                  <a 
                    href="https://lin.ee/MJ3LVM2" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="line-btn-primary inline-flex items-center justify-center space-x-3 text-base font-medium whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-line-fill text-xl"></i>
                    </div>
                    <span>LINE で応募する</span>
                  </a>
                  <a 
                    href="tel:052-887-6243"
                    className="bg-white border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-full inline-flex items-center justify-center space-x-3 text-base font-medium hover:bg-blue-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-phone-line text-xl"></i>
                    </div>
                    <span>電話で応募する</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {(showPasswordInput || showEditForm) && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            {!showEditForm ? (
              <div className="max-w-md">
                <h3 className="text-xl font-bold text-gray-900 mb-4">管理者ログイン</h3>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  パスワードを入力してください
                </label>
                <div className="flex gap-3">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                    placeholder="パスワード"
                  />
                  <button
                    onClick={handlePasswordSubmit}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    確認
                  </button>
                </div>
                {passwordError && (
                  <p className="text-red-600 text-sm mt-2">{passwordError}</p>
                )}
                <button
                  onClick={() => {
                    setShowPasswordInput(false);
                    setPassword('');
                    setPasswordError('');
                  }}
                  className="mt-4 text-gray-600 hover:text-gray-800 text-sm cursor-pointer"
                >
                  キャンセル
                </button>
              </div>
            ) : (
              <JobEditForm 
                job={job!} 
                onClose={() => {
                  setShowEditForm(false);
                  setPassword('');
                }} 
                onUpdate={handleJobUpdate}
              />
            )}
          </div>
        )}
      </div>

      <Footer 
        onAdminPress={startPress}
        onAdminRelease={cancelPress}
      />
      <FloatingLineButton />
    </div>
  );
}
